"use client";

import { useEffect, useRef, useState } from "react";
import type { FeedbackContext } from "@/components/aicheck/shared/StructuredResultView";
import { FeedbackButtons } from "@/components/aicheck/shared/StructuredResultView";
import { apiUrl } from "@/lib/aicheck/api-base";
import type {
  CheckAspect,
  FactAssertion,
} from "@/lib/aicheck/expert-knowledge/types";
import { authService } from "@/services/auth";
import { useExpertFeedbackStore } from "@/stores/expertFeedback";
import type { AICheckRunResult } from "@/types/aicheck";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpertFinding {
  severity?: string;
  finding?: string;
  recommendation?: string;
  aspectId?: string;
  usedFactIds?: string[];
  [key: string]: unknown;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function severityBorderClass(severity?: string): string {
  if (severity === "critical") return "border-red-200 bg-red-50/50";
  if (severity === "warning") return "border-yellow-200 bg-yellow-50/50";
  return "border-gray-200 bg-gray-50/50";
}

function severityBadgeClass(severity?: string): string {
  if (severity === "critical") return "bg-red-100 text-red-700";
  if (severity === "warning") return "bg-yellow-100 text-yellow-700";
  return "bg-gray-100 text-gray-600";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// ─── Auth-aware fetch helper ──────────────────────────────────────────────────

async function authedFetch(url: string): Promise<Response> {
  const token = await authService.getIdToken();
  if (!token) throw new Error("未認証です。再度サインインしてください。");
  return fetch(apiUrl(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ExpertKnowledgeView({ result }: { result: AICheckRunResult }) {
  // --- Extract expert-nuance-check result ---
  const expertResult = result.aiResults.find(
    (r) => r.checkId === "expert-nuance-check",
  );

  const rawFindings: unknown[] = Array.isArray(expertResult?.result?.findings)
    ? (expertResult.result.findings as unknown[])
    : [];

  const findings: ExpertFinding[] = rawFindings.filter(
    (f): f is ExpertFinding => typeof f === "object" && f !== null,
  );

  const feedbackContext: FeedbackContext = {
    runId: (result as { runId?: string }).runId,
    projectId: result.projectId != null ? String(result.projectId) : undefined,
  };

  // --- Aspects fetch ---
  const [aspects, setAspects] = useState<Map<string, CheckAspect>>(new Map());
  const [factsMap, setFactsMap] = useState<Map<string, FactAssertion>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  // --- Store ---
  const { loadStatus } = useExpertFeedbackStore();

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchData() {
      setLoading(true);
      setLoadError(null);
      try {
        // Fetch aspects and facts in parallel
        const [aspectsRes, factsRes] = await Promise.all([
          authedFetch("/api/aicheck/expert-knowledge/aspects"),
          authedFetch("/api/aicheck/expert-knowledge/facts"),
        ]);

        if (aspectsRes.ok) {
          const data = (await aspectsRes.json()) as {
            ok: boolean;
            aspects?: CheckAspect[];
          };
          if (data.ok && Array.isArray(data.aspects)) {
            const map = new Map<string, CheckAspect>();
            for (const a of data.aspects) {
              map.set(a.slug, a);
              map.set(a.id, a);
            }
            setAspects(map);
          }
        }

        if (factsRes.ok) {
          const data = (await factsRes.json()) as {
            ok: boolean;
            facts?: FactAssertion[];
          };
          if (data.ok && Array.isArray(data.facts)) {
            const map = new Map<string, FactAssertion>();
            for (const f of data.facts) {
              map.set(f.id, f);
            }
            setFactsMap(map);
          }
        }
      } catch {
        setLoadError("観点・事実データの読み込みに失敗しました。");
      } finally {
        setLoading(false);
      }
    }

    void fetchData();
  }, []);

  // Load persisted feedback status once aspects + findings are available
  useEffect(() => {
    if (!feedbackContext.projectId) return;
    const aspectIds = findings
      .map((f) => f.aspectId)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    if (aspectIds.length > 0) {
      void loadStatus(feedbackContext.projectId, aspectIds);
    }
  }, [feedbackContext.projectId, findings, loadStatus]);

  // --- Render ---

  if (!expertResult) {
    return (
      <div className="py-12 text-center text-gray-400">
        <p className="text-base font-medium text-gray-500 mb-1">
          Expert Knowledge チェックは実行されませんでした
        </p>
        <p className="text-sm">
          Expert Knowledge
          ルールが登録されていないか、このプロジェクトに該当するルールがありませんでした。
        </p>
      </div>
    );
  }

  if (findings.length === 0) {
    return (
      <div className="py-12 text-center text-gray-400">
        <p className="text-base font-medium text-gray-500 mb-1">指摘事項なし</p>
        <p className="text-sm">
          Expert Knowledge ルールが該当しませんでした。品質的に問題ありません。
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-8 text-center text-gray-400 text-sm">
        観点・事実データを読み込み中...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loadError && (
        <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
          {loadError}
          （観点・事実の詳細は表示できませんが、指摘内容は確認できます）
        </div>
      )}

      <p className="text-xs text-gray-500">
        {findings.length} 件の Expert Knowledge
        指摘があります。フィードバックにご協力ください。
      </p>

      {findings.map((f, i) => {
        const aspect = f.aspectId ? aspects.get(f.aspectId) : undefined;
        const factIds = Array.isArray(f.usedFactIds)
          ? (f.usedFactIds as string[])
          : [];
        const facts = factIds
          .map((id) => factsMap.get(id))
          .filter((fact): fact is FactAssertion => fact !== undefined);

        return (
          <div
            key={`ek-finding-${i}`}
            className={`rounded-md border px-4 py-3 text-sm ${severityBorderClass(f.severity)}`}
          >
            {/* Severity badge */}
            {f.severity && (
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`text-xs font-medium px-1.5 py-0.5 rounded ${severityBadgeClass(f.severity)}`}
                >
                  {f.severity}
                </span>
                {aspect && (
                  <span className="text-xs text-gray-400 font-mono">
                    [{aspect.slug}] {aspect.title}
                  </span>
                )}
              </div>
            )}

            {/* Aspect info (when no severity badge shown it above) */}
            {!f.severity && aspect && (
              <div className="mb-2">
                <span className="text-xs text-gray-400 font-mono">
                  [{aspect.slug}] {aspect.title}
                </span>
              </div>
            )}

            {/* Finding text */}
            {f.finding && <p className="text-gray-700 mb-2">{f.finding}</p>}

            {/* Recommendation */}
            {f.recommendation && (
              <p className="text-xs text-gray-500 mb-2">
                {"\u{1F4A1}"} {f.recommendation}
              </p>
            )}

            {/* Used facts */}
            {facts.length > 0 && (
              <div className="mt-2 mb-1">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                  根拠事実
                </div>
                <ul className="space-y-0.5">
                  {facts.map((fact) => (
                    <li
                      key={fact.id}
                      className="text-xs text-gray-500 flex items-start gap-1.5"
                    >
                      <span className="text-gray-400 shrink-0">・</span>
                      <span>
                        <span className="font-medium text-gray-600">
                          {fact.subject}
                        </span>
                        {" — "}
                        {fact.predicate}
                        <span className="text-gray-400 ml-1">
                          （観測日: {formatDate(fact.observedAt)}）
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Feedback buttons */}
            {typeof f.aspectId === "string" && f.aspectId.length > 0 && (
              <FeedbackButtons
                aspectId={f.aspectId}
                usedFactIds={factIds}
                feedbackCtx={feedbackContext}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
